import { atom } from "jotai";
import { DateTime } from "luxon";
import { DateRange } from "react-day-picker";

export interface OrgLevelFilters {
  selectedEvents: string[];
  selectedStatuses: string[];
  selectedActors: string[];
  selectedRepos: string[];
  selectedWorkflows: string[];
  selectedBranches: string[];
  selectedJobs: string[];
  selectedRunnerTypes: string[];
  runType: "workflow" | "job";
}

export const defaultOrgFilters: OrgLevelFilters = {
  selectedEvents: [],
  selectedStatuses: [],
  selectedActors: [],
  selectedRepos: [],
  selectedWorkflows: [],
  selectedBranches: [],
  selectedJobs: [],
  selectedRunnerTypes: [],
  runType: "workflow",
};

export const runsOrgFiltersAtom = atom(defaultOrgFilters);

export const recentTimeTabAtom = atom("1D");

// Default global tab atom for analytics pages (MTD, 7D, 30D, etc.)
export const tabAtom = atom("MTD");

// Flips to true the first time the user explicitly picks a date-range window
// (a preset tab or a custom range) in this session. The date-range atoms start
// at their track defaults (MTD / 1D), so their value alone can't distinguish an
// untouched default from a user who deliberately re-selected the default. Pages
// that seed a page-specific default window (e.g. the repository cache page's
// rolling 30D) read this to avoid overriding a window the user actually chose.
export const dateRangeUserTouchedAtom = atom(false);

const getDefaultDateRange = () => ({
  from: DateTime.now().minus({ days: 1 }).startOf("minute").toJSDate(),
  to: DateTime.now().endOf("minute").toJSDate(),
});

export const recentDateRangeAtom = atom<DateRange>(getDefaultDateRange());

// startDateAtom is a derived read-only atom that returns the start datetime of the current date range in UTC.
export const recentStartDateAtom = atom<Date>((get) => {
  const dateRange = get(recentDateRangeAtom);
  return dateRange.from ?? getDefaultDateRange().from;
});

// endDateAtom is a derived read-only atom that returns the end datetime of the current date range in UTC.
export const recentEndDateAtom = atom<Date>((get) => {
  const dateRange = get(recentDateRangeAtom);
  return dateRange.to ?? DateTime.now().toJSDate();
});

export function getOrgQueryParams(filters: OrgLevelFilters): URLSearchParams {
  const params = new URLSearchParams();

  const paramMappings: Array<{ key: string; values: string[] }> = [
    { key: "events[]", values: filters.selectedEvents },
    { key: "statuses[]", values: filters.selectedStatuses },
    { key: "actors[]", values: filters.selectedActors },
    { key: "repositories[]", values: filters.selectedRepos },
    { key: "workflows[]", values: filters.selectedWorkflows },
    { key: "branches[]", values: filters.selectedBranches },
    { key: "jobs[]", values: filters.selectedJobs },
    { key: "runner_types[]", values: filters.selectedRunnerTypes },
  ];

  for (const { key, values } of paramMappings) {
    if (values.length) {
      for (const value of values) {
        params.append(key, value);
      }
    }
  }

  return params;
}
